import type { GetMainMenuItemsParams, MenuItemDef } from 'ag-grid-enterprise';
import {
  APIFieldAttributes,
  APIComponentAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { Maybe } from '@ardoq/common-helpers';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import type { InventoryContext } from '../types';
import { Locale } from '@ardoq/locale';
import { formatDateTimeShort } from '@ardoq/date-time';
import { toColumnMenuItemDef } from './CustomColumnMenuItem';
import { cellRendererPropsOperations } from '../CellRenderers/cellRendererPropsOperations';
import { inventoryDatasourceOperations } from '../datasourceOperations';
import { type I18nLocale, t } from '@ardoq/i18n';

const getFieldFromParams = (
  params: GetMainMenuItemsParams<APIComponentAttributes, InventoryContext>
): Maybe<APIFieldAttributes> => {
  const scopeData = params.context.scopeData;
  const fieldName = params.column?.getColDef().field;

  if (!fieldName) {
    return null;
  }

  return enhancedScopeDataOperations.getFieldByName(scopeData, fieldName);
};

const getLastRecalculationFormatted = (
  fieldId: ArdoqId,
  lastRecalculationByFieldsId: Record<ArdoqId, string>,
  locale: Locale,
  i18nLocale: I18nLocale
) => {
  const lastRecalculation = lastRecalculationByFieldsId[fieldId];

  if (!lastRecalculation) {
    return t(i18nLocale, 'inventoryApp:ag.columnMenu.never', 'Never');
  }

  return formatDateTimeShort(lastRecalculation, locale);
};

export const getRecalculateMainMenuItem = (
  params: GetMainMenuItemsParams<APIComponentAttributes, InventoryContext>
): MenuItemDef<APIComponentAttributes> | null => {
  const {
    config: { locale },
    accessControl: { canEditWorkspace },
    datasourceSelection,
    commands,
    scopeData,
    lastRecalculationByFieldsId,
  } = params.context;

  // AG Grid external-lib callback: read locale from context per invocation.
  const i18nLocale = params.context.i18nLocale ?? {};

  const isReportDatasource =
    inventoryDatasourceOperations.isReportInventoryDatasourceSelection(
      datasourceSelection
    );

  if (isReportDatasource) {
    return null;
  }

  if (
    !cellRendererPropsOperations.isCalculatedColumn(params.column?.getColDef())
  ) {
    return null;
  }

  const field = getFieldFromParams(params);
  if (!field) {
    return null;
  }

  const workspaceId =
    enhancedScopeDataOperations.getWorkspaceIdByComponentModelId(
      scopeData,
      field.model
    );
  const hasEditPermission = workspaceId && canEditWorkspace(workspaceId);

  const fieldId = field._id;
  const lastRecalculationFormatted = getLastRecalculationFormatted(
    fieldId,
    lastRecalculationByFieldsId,
    locale,
    i18nLocale
  );

  return toColumnMenuItemDef({
    name: t(
      i18nLocale,
      'inventoryApp:ag.columnMenu.recalculateField',
      'Recalculate field'
    ),
    action: () => commands.recalculateField(fieldId),
    disabled: !hasEditPermission,
    tooltip: hasEditPermission
      ? undefined
      : t(
          i18nLocale,
          'inventoryApp:ag.columnMenu.recalculateNoPermission',
          "You don't have permission to recalculate this field. Contact your admin to request access."
        ),
    description: t(
      i18nLocale,
      'inventoryApp:ag.columnMenu.lastCalculation',
      { when: lastRecalculationFormatted },
      'Last calculation: {{when}}'
    ),
  });
};
